import { computed } from 'vue';
import type { useQuoteGeneration } from '@/composables/useQuoteGeneration';
import type { useQuoteStore } from '@/stores/quoteStore';
import { translateInsuranceError, extractCompanyFromError } from '@/utils/errorMessages';

type QuoteGeneration = ReturnType<typeof useQuoteGeneration>;
type QuoteStore = ReturnType<typeof useQuoteStore>;

/** Error dialog state derived from the store error and the quote generation error. */
export const useWidgetError = (quoteStore: QuoteStore, quoteGeneration: QuoteGeneration) => {
  const currentError = () => quoteStore.error || quoteGeneration.error.value;

  const translatedErrorMessage = computed(() => {
    const rawError = currentError();
    if (!rawError) return '';

    const companyName = extractCompanyFromError(rawError);
    return translateInsuranceError(rawError, companyName);
  });

  const hasWidgetError = computed(() => !!currentError());

  const errorDialogTitle = computed(() => {
    const raw = currentError();
    if (!raw) return 'Aviso';
    if (raw.toLowerCase().includes('ninguna aseguradora')) {
      return 'Sin cotizaciones disponibles';
    }
    return 'No pudimos continuar';
  });

  const handleCloseError = (): void => {
    quoteStore.setError(null);
    quoteGeneration.error.value = null;
  };

  const onErrorDialogVisibleChange = (visible: boolean): void => {
    if (!visible) {
      handleCloseError();
    }
  };

  return {
    translatedErrorMessage,
    hasWidgetError,
    errorDialogTitle,
    handleCloseError,
    onErrorDialogVisibleChange,
  };
};
